import React, { useEffect } from 'react';
import { Typography, Box } from '@mui/material';
import { useSelector } from 'react-redux';

// Components
import Rating from '../../Rating';
import Button from '../../Button';
import Feather from '../../Feather';
import Image from '../../Image';
import ProductDetailsButtons from './ProductDetailsButtons';
import ProductInfo, { QuantityInfo } from './ProductInfo';
import MyRating from '../../Rating/MyRating';
import NewLabel from '../Card/NewLabel';
import ReportableIcon from '../Card/ReportableIcon';
import FdmButton from '../Card/FdmButton';

// Hooks
import useUser from '../../../hooks/useUser';
import useCart from '../../../pages/Cart/hooks/useCart';
import useRequest from '../../../pages/Cart/hooks/useRequest';
import useUI from '../../../hooks/useUI';
import useFavorites from '../../../hooks/useFavorites';
import useUnavailableForOrderProduct from '../../../hooks/useUnavailableForOrderProduct';
import useOverLimitProduct from '../../../pages/Cart/hooks/useOverLimitProduct';

// Utils
import isDigitalAsset from '../../../utils/isDigitalAsset';

// Selectors
import { productsObjSelector } from '../../../store/selectors';

// Styles
import { useStyles } from './DetailsStyles';

const DETAILS_IMAGE_WIDTH_DESKTOP = 290;
const DETAILS_IMAGE_WIDTH_TABLET = 363;
const DETAILS_IMAGE_WIDTH_MOBILE = 320;

const MobileDetails = ({
  product,
  classes,
  isMobileMode,
  cartProps,
  withMyRating,
  ignoreCatalogAccess = false,
  hideFavorites = false,
}) => {
  const {
    image_link: image,
    product_description: description,
    is_new: isNew,
    is_favourite: isFavorite,
    rating: isRatable,
    reportable: isReportable,
    size_vers: sizeVers,
  } = product || {};

  const { setFavoriteValue } = useFavorites();
  const { isUnavailableForOrder, unavailableLabel, unavailableWatermark } =
    useUnavailableForOrderProduct(
      product,
      false,
      true,
      false,
      ignoreCatalogAccess,
    );

  return (
    <>
      <Box display="flex" justifyContent="center">
        <div className={classes.imageContainer}>
          {isNew && <NewLabel />}
          {unavailableWatermark}
          <Image alt="image" src={image} />
        </div>
      </Box>
      {!hideFavorites && (
        <Button
          data-testid="add-to-favorites"
          className={classes.addToFavorite}
          variant="text"
          onClick={() =>
            setFavoriteValue(product?.product_id, !isFavorite, true)
          }>
          <Feather type="flag" printType="stroke" />
          {isFavorite ? 'In Favorites' : 'Add To Favorites'}
        </Button>
      )}
      {isRatable && withMyRating && (
        <MyRating product={product} className={classes.myRating} />
      )}
      <div className={classes.descAndSize}>
        <Typography className={classes.description} variant="h2">
          {description}
        </Typography>
        <Typography variant="textSm">{sizeVers}</Typography>
      </div>
      {isReportable && (
        <div className={classes.reportableRow}>
          <ReportableIcon withText />
        </div>
      )}
      {isRatable && <Rating product={product} />}
      <FdmButton
        styles={{
          fontSize: 14,
          marginBottom: 20,
          marginTop: 28,
        }}
        className={classes.fdmButton}
        product={product}
      />
      <div className={classes.mobileQuantity}>
        <QuantityInfo product={product} unavailableLabel={unavailableLabel} />
      </div>
      <ProductInfo product={product} isMobileMode={isMobileMode} />
      {!isUnavailableForOrder && (
        <div className={classes.buttons}>
          <ProductDetailsButtons
            product={product}
            isMobileMode={isMobileMode}
            cartProps={cartProps}
            detailsMode
          />
        </div>
      )}
    </>
  );
};

const Details = ({
  productId,
  withMyRating = true,
  showRequestModal,
  kitItemOrderable,
  readOnly = false,
}) => {
  const { isKiteUser } = useUser();
  const { isDesktopMode, isTabletMode, isMobileMode } = useUI();
  const product = useSelector(
    (state) => productsObjSelector(state)?.[productId],
  );
  const {
    addInCart,
    addingProgress,
    count,
    addOne,
    removeOne,
    setCount,
    isProductInCart,
  } = useCart(product?.product_id);
  const { isOverLimitProduct } = useOverLimitProduct(product?.product_id);
  const { openRequestCard, savedRequest } = useRequest(
    product?.product_id,
    true,
  );

  const isDigitalAssetProduct = isDigitalAsset(product);
  const ignoreCatalogAccess = isDigitalAssetProduct || !!kitItemOrderable;

  const { isUnavailableForOrder, unavailableLabel, unavailableWatermark } =
    useUnavailableForOrderProduct(
      product,
      false,
      true,
      false,
      ignoreCatalogAccess,
    );

  const isOutsideCatalog = !product?.is_available_for_user;

  const isViewOnlyDigitalAsset = isDigitalAssetProduct && isOutsideCatalog;

  useEffect(() => {
    if (isOverLimitProduct && !savedRequest && showRequestModal) {
      openRequestCard();
    }
  }, [openRequestCard, isOverLimitProduct, savedRequest, showRequestModal]);

  const {
    image_link: image,
    product_description: description,
    product_id: stockNumber,
    is_new: isNew,
    is_favourite: isFavorite,
    rating: isRatable,
    reportable: isReportable,
    size_vers: sizeVers,
  } = product || {};

  const { setFavoriteValue } = useFavorites();

  const { classes, cx } = useStyles({
    isKiteUser,
    isUnavailableForOrder,
    isFavorite,
  });

  const renderTabletModeDescription = () => {
    return (
      isTabletMode && (
        <div>
          <Typography className={classes.description} variant="h2">
            {description}
          </Typography>
          <Typography variant="textSm">{sizeVers}</Typography>
          {isRatable && <Rating withTooltip={false} product={product} />}
        </div>
      )
    );
  };
  const renderDesktopModeDescription = () => {
    return (
      isDesktopMode && (
        <>
          <Typography className={classes.description} variant="h2">
            {description}
          </Typography>
          <Typography variant="textSm">{sizeVers}</Typography>
          {isRatable && <Rating withTooltip={false} product={product} />}
        </>
      )
    );
  };
  const renderFavoriteButton = () => {
    return (
      !isOutsideCatalog && (
        <Button
          className={classes.addToFavorite}
          variant="text"
          onClick={() =>
            setFavoriteValue(product?.product_id, !isFavorite, true)
          }>
          <Feather type="flag" printType="stroke" />
          {isFavorite ? 'In Favorites' : 'Add To Favorites'}
        </Button>
      )
    );
  };
  const renderRating = () => {
    return (
      isRatable &&
      withMyRating && (
        <MyRating product={product} className={classes.myRating} />
      )
    );
  };
  const renderTableModeDetailsButton = () => {
    return (
      isTabletMode && (
        <div className={cx(classes.buttons, classes.buttonsColumn)}>
          <ProductDetailsButtons
            product={product}
            isMobileMode={isMobileMode}
            detailsMode
            hideQtyButtons={isUnavailableForOrder || readOnly}
            cartProps={{
              loading: addingProgress,
              addInCart,
              count,
              addOne,
              removeOne,
              setCount,
              isProductInCart,
              isOverLimit: isOverLimitProduct,
            }}
          />
        </div>
      )
    );
  };
  const renderDesktopModeDetailsButton = () => {
    return (
      isDesktopMode &&
      !isViewOnlyDigitalAsset && (
        <div className={classes.buttons}>
          <ProductDetailsButtons
            product={product}
            isMobileMode={isMobileMode}
            detailsMode
            hideQtyButtons={isUnavailableForOrder || readOnly}
            cartProps={{
              loading: addingProgress,
              addInCart,
              count,
              addOne,
              removeOne,
              setCount,
              isProductInCart,
              isOverLimit: isOverLimitProduct,
            }}
          />
        </div>
      )
    );
  };
  return (
    <>
      {!isMobileMode ? (
        <div className={classes.root}>
          {renderTabletModeDescription()}
          <div className={classes.flexContainer}>
            <div className={classes.left}>
              {renderDesktopModeDescription()}
              <Typography className={classes.stockNumber} variant="h3">
                {stockNumber && `Stock Number: ${stockNumber}`}
              </Typography>
              <ProductInfo product={product} />
              {renderDesktopModeDetailsButton()}
            </div>
            <div className={classes.right}>
              <QuantityInfo
                product={product}
                unavailableLabel={unavailableLabel}
                isDigitalAsset={isDigitalAssetProduct}
              />
              {renderFavoriteButton()}
              <div className={classes.imageContainer}>
                {isNew && <NewLabel />}
                {unavailableWatermark}
                <Image
                  alt="image"
                  src={image}
                  imageContainer={{
                    desktop: [DETAILS_IMAGE_WIDTH_DESKTOP],
                    tablet: [DETAILS_IMAGE_WIDTH_TABLET],
                    mobile: [DETAILS_IMAGE_WIDTH_MOBILE],
                  }}
                />
                {isReportable && <ReportableIcon withText />}
                {renderRating()}
              </div>
            </div>
          </div>
          {renderTableModeDetailsButton()}
        </div>
      ) : (
        <MobileDetails
          ignoreCatalogAccess={ignoreCatalogAccess}
          hideFavorites={isOutsideCatalog}
          withMyRating={withMyRating}
          product={product}
          classes={classes}
          isMobileMode={isMobileMode}
          cartProps={{
            addInCart,
            count,
            addOne,
            removeOne,
            setCount,
            isProductInCart,
            isOverLimit: isOverLimitProduct,
          }}
        />
      )}
    </>
  );
};

export default Details;
